import { Injectable } from '@angular/core';
import {
  BillingSubtotal,
  ClientBillingRow,
  CountryOperatorRow,
  ExactDecimal,
  InfobipDataValidationError,
  InfobipFilterOptions,
  InfobipReportApiRecord,
  InfobipReportFilters,
  InfobipReportRecord,
  InfobipReportsResponse,
  InfobipReportView,
  InfobipValidationIssue,
  SmsBillingRow,
  SpendPoint,
  WhatsappBillingRow
} from './infobip-report.models';

const EMPTY_LABEL = 'Sin especificar';
const DECIMAL_PATTERN = /^-?\d+(?:\.\d+)?$/;

@Injectable({ providedIn: 'root' })
export class InfobipReportAnalysisService {
  readonly emptyLabel = EMPTY_LABEL;

  normalizeResponse(value: unknown): { totalRecords: number; records: InfobipReportRecord[] } {
    if (!this.isRecord(value) || !Array.isArray(value['reports'])) {
      throw new Error('La respuesta del servicio de Infobip no tiene el formato esperado.');
    }

    const totalRecords = Number(value['totalRecords']);
    if (!Number.isFinite(totalRecords)) {
      throw new Error('La respuesta del servicio de Infobip no incluye un total válido.');
    }

    const issues: InfobipValidationIssue[] = [];
    const records = value['reports'].map((candidate, index) => this.normalizeRecord(candidate, index, issues));
    if (issues.length > 0) {
      throw new InfobipDataValidationError(issues);
    }

    return { totalRecords, records: records as InfobipReportRecord[] };
  }

  buildFilterOptions(records: InfobipReportRecord[]): InfobipFilterOptions {
    return {
      accounts: this.options(records.map(row => row.accountName), 'Todos los clientes'),
      services: this.options(records.map(row => row.service), 'Todos los servicios'),
      trafficTypes: this.options(records.map(row => row.normalizedTrafficType), 'Todos los tipos'),
      directions: this.options(records.map(row => row.normalizedDirection), 'Todas las direcciones'),
      countries: this.options(records.map(row => row.country), 'Todos los países'),
      operators: this.options(records.map(row => row.operator), 'Todos los operadores')
    };
  }

  buildView(source: InfobipReportRecord[], filters: InfobipReportFilters): InfobipReportView {
    const records = source.filter(row =>
      (!filters.account || row.accountName === filters.account)
      && (!filters.service || row.service === filters.service)
      && (!filters.trafficType || row.normalizedTrafficType === filters.trafficType)
      && (!filters.direction || row.normalizedDirection === filters.direction)
      && (!filters.country || row.country === filters.country)
      && (!filters.operator || row.operator === filters.operator)
    );

    const sms = records.filter(row => row.service === 'SMS');
    const whatsapp = records.filter(row => row.service === 'WHATSAPP');
    const clients = this.buildClients(records);
    const smsRows = this.buildSmsRows(sms);
    const whatsappRows = this.buildWhatsappRows(whatsapp);

    return {
      records,
      summary: {
        totalAmount: this.sum(records.map(row => row.totalAmountValue)),
        smsAmount: this.sum(sms.map(row => row.totalAmountValue)),
        whatsappAmount: this.sum(whatsapp.map(row => row.totalAmountValue)),
        smsQuantity: this.sum(sms.map(row => row.quantityValue)),
        whatsappQuantity: this.sum(whatsapp.map(row => row.quantityValue)),
        clientCount: new Set(records.map(row => row.accountName)).size,
        recordCount: records.length
      },
      clients,
      smsRows,
      smsClientSubtotals: this.subtotalsByClient(smsRows),
      whatsappRows,
      whatsappTrafficSubtotals: this.subtotalsByClientAndTraffic(whatsappRows),
      whatsappClientSubtotals: this.subtotalsByClient(whatsappRows),
      countryOperatorRows: this.buildCountryOperatorRows(records),
      dailySpend: this.spendPoints(records, row => row.usageDay),
      serviceSpend: this.spendPoints(records, row => row.service),
      clientSpend: this.spendPoints(records, row => row.accountName)
    };
  }

  zero(): ExactDecimal {
    return { coefficient: 0n, scale: 0 };
  }

  add(left: ExactDecimal, right: ExactDecimal): ExactDecimal {
    const scale = Math.max(left.scale, right.scale);
    const leftCoefficient = left.coefficient * (10n ** BigInt(scale - left.scale));
    const rightCoefficient = right.coefficient * (10n ** BigInt(scale - right.scale));
    return this.normalizeDecimal({ coefficient: leftCoefficient + rightCoefficient, scale });
  }

  sum(values: ExactDecimal[]): ExactDecimal {
    return values.reduce((total, value) => this.add(total, value), this.zero());
  }

  toNumber(value: ExactDecimal): number {
    return Number(value.coefficient) / (10 ** value.scale);
  }

  formatDecimal(value: ExactDecimal, minimumFractionDigits = 0, maximumFractionDigits = 6): string {
    return this.toNumber(value).toLocaleString('es-MX', {
      minimumFractionDigits,
      maximumFractionDigits
    });
  }

  decimalEquals(left: ExactDecimal, right: ExactDecimal): boolean {
    const difference = this.add(left, { coefficient: -right.coefficient, scale: right.scale });
    return difference.coefficient === 0n;
  }

  private normalizeRecord(
    value: unknown,
    index: number,
    issues: InfobipValidationIssue[]
  ): InfobipReportRecord | null {
    if (!this.isRecord(value)) {
      issues.push({ row: index + 1, field: 'record', value, message: 'El registro no es un objeto.' });
      return null;
    }

    const id = Number(value['id']);
    const issue = (field: string, candidate: unknown, message: string) => {
      issues.push({ row: index + 1, id: Number.isFinite(id) ? id : undefined, field, value: candidate, message });
    };

    const accountName = this.requiredText(value['accountName']);
    const categoryCode = this.requiredText(value['categoryCode']);
    const usageDate = this.requiredText(value['usageDate']);
    const unitType = this.requiredText(value['unitType']);
    const currency = this.requiredText(value['currency']).toUpperCase();
    if (!Number.isFinite(id)) issue('id', value['id'], 'El identificador no es válido.');
    if (!accountName) issue('accountName', value['accountName'], 'El cliente es obligatorio.');
    if (!categoryCode) issue('categoryCode', value['categoryCode'], 'El servicio es obligatorio.');
    if (!/^\d{4}-\d{2}-\d{2}/.test(usageDate)) issue('usageDate', value['usageDate'], 'La fecha no es válida.');
    if (!unitType) issue('unitType', value['unitType'], 'La unidad es obligatoria.');
    if (currency !== 'USD') issue('currency', value['currency'], 'Solo se admite moneda USD.');

    const quantityValue = this.parseDecimal(value['quantity'], index, id, 'quantity', issues);
    const unitPriceValue = this.parseDecimal(value['unitPrice'], index, id, 'unitPrice', issues);
    const totalAmountValue = this.parseDecimal(value['totalAmount'], index, id, 'totalAmount', issues);
    if (!quantityValue || !unitPriceValue || !totalAmountValue || !accountName || !categoryCode || !usageDate || !unitType) {
      return null;
    }

    return {
      id,
      accountName,
      categoryCode,
      usageDate,
      billingTimeUnit: this.nullableText(value['billingTimeUnit']),
      unitType,
      unitPrice: String(value['unitPrice']),
      quantity: String(value['quantity']),
      totalAmount: String(value['totalAmount']),
      currency,
      countryCode: this.nullableText(value['countryCode']),
      countryName: this.nullableText(value['countryName']),
      direction: this.nullableText(value['direction']),
      networkName: this.nullableText(value['networkName']),
      trafficType: this.nullableText(value['trafficType']),
      usageDay: usageDate.slice(0, 10),
      service: categoryCode.toUpperCase(),
      country: this.displayText(value['countryName']),
      operator: this.displayText(value['networkName']),
      normalizedDirection: this.displayText(value['direction']).toUpperCase(),
      normalizedTrafficType: this.displayText(value['trafficType']).toUpperCase(),
      quantityValue,
      unitPriceValue,
      totalAmountValue
    };
  }

  private parseDecimal(
    value: unknown,
    index: number,
    id: number,
    field: string,
    issues: InfobipValidationIssue[]
  ): ExactDecimal | null {
    const text = typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';
    if (!DECIMAL_PATTERN.test(text)) {
      issues.push({ row: index + 1, id: Number.isFinite(id) ? id : undefined, field, value, message: 'El decimal no es válido.' });
      return null;
    }

    const negative = text.startsWith('-');
    const unsigned = negative ? text.slice(1) : text;
    const [integer, fraction = ''] = unsigned.split('.');
    const coefficient = BigInt(`${negative ? '-' : ''}${integer}${fraction}`);
    return this.normalizeDecimal({ coefficient, scale: fraction.length });
  }

  private normalizeDecimal(value: ExactDecimal): ExactDecimal {
    let { coefficient, scale } = value;
    while (scale > 0 && coefficient % 10n === 0n) {
      coefficient /= 10n;
      scale -= 1;
    }
    return { coefficient, scale };
  }

  private buildClients(records: InfobipReportRecord[]): ClientBillingRow[] {
    const groups = this.group(records, row => row.accountName);
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b, 'es')).map(([accountName, details]) => {
      const sms = details.filter(row => row.service === 'SMS');
      const whatsapp = details.filter(row => row.service === 'WHATSAPP');
      return {
        accountName,
        smsQuantity: this.sum(sms.map(row => row.quantityValue)),
        whatsappQuantity: this.sum(whatsapp.map(row => row.quantityValue)),
        smsAmount: this.sum(sms.map(row => row.totalAmountValue)),
        whatsappAmount: this.sum(whatsapp.map(row => row.totalAmountValue)),
        totalAmount: this.sum(details.map(row => row.totalAmountValue)),
        details: [...details].sort((a, b) => a.usageDay.localeCompare(b.usageDay))
      };
    });
  }

  private buildSmsRows(records: InfobipReportRecord[]): SmsBillingRow[] {
    const groups = this.group(records, row => [row.accountName, row.country, row.operator, this.decimalKey(row.unitPriceValue)].join('\u0001'));
    return [...groups.values()].map(rows => ({
      accountName: rows[0].accountName,
      country: rows[0].country,
      operator: rows[0].operator,
      unitPrice: rows[0].unitPrice,
      unitPriceValue: rows[0].unitPriceValue,
      quantity: this.sum(rows.map(row => row.quantityValue)),
      amount: this.sum(rows.map(row => row.totalAmountValue))
    })).sort((a, b) => this.compareFields(a, b, ['accountName', 'country', 'operator', 'unitPrice']));
  }

  private buildWhatsappRows(records: InfobipReportRecord[]): WhatsappBillingRow[] {
    const groups = this.group(records, row => [
      row.accountName, row.normalizedDirection, row.normalizedTrafficType,
      row.country, row.operator, this.decimalKey(row.unitPriceValue)
    ].join('\u0001'));
    return [...groups.values()].map(rows => ({
      accountName: rows[0].accountName,
      direction: rows[0].normalizedDirection,
      trafficType: rows[0].normalizedTrafficType,
      country: rows[0].country,
      operator: rows[0].operator,
      unitPrice: rows[0].unitPrice,
      unitPriceValue: rows[0].unitPriceValue,
      quantity: this.sum(rows.map(row => row.quantityValue)),
      amount: this.sum(rows.map(row => row.totalAmountValue))
    })).sort((a, b) => this.compareFields(a, b, ['accountName', 'trafficType', 'country', 'operator', 'unitPrice']));
  }

  private subtotalsByClient(rows: Array<SmsBillingRow | WhatsappBillingRow>): BillingSubtotal[] {
    const groups = this.group(rows, row => row.accountName);
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b, 'es')).map(([accountName, values]) => ({
      accountName,
      quantity: this.sum(values.map(row => row.quantity)),
      amount: this.sum(values.map(row => row.amount))
    }));
  }

  private subtotalsByClientAndTraffic(rows: WhatsappBillingRow[]): BillingSubtotal[] {
    const groups = this.group(rows, row => `${row.accountName}\u0001${row.trafficType}`);
    return [...groups.values()].map(values => ({
      accountName: values[0].accountName,
      trafficType: values[0].trafficType,
      quantity: this.sum(values.map(row => row.quantity)),
      amount: this.sum(values.map(row => row.amount))
    })).sort((a, b) => `${a.accountName}${a.trafficType}`.localeCompare(`${b.accountName}${b.trafficType}`, 'es'));
  }

  private buildCountryOperatorRows(records: InfobipReportRecord[]): CountryOperatorRow[] {
    const groups = this.group(records, row => [row.country, row.operator, row.service, row.unitType].join('\u0001'));
    return [...groups.values()].map(rows => ({
      country: rows[0].country,
      operator: rows[0].operator,
      service: rows[0].service,
      unitType: rows[0].unitType,
      quantity: this.sum(rows.map(row => row.quantityValue)),
      amount: this.sum(rows.map(row => row.totalAmountValue))
    })).sort((a, b) => this.compareFields(a, b, ['country', 'operator', 'service', 'unitType']));
  }

  private spendPoints(records: InfobipReportRecord[], selector: (row: InfobipReportRecord) => string): SpendPoint[] {
    const groups = this.group(records, selector);
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b, 'es')).map(([key, rows]) => ({
      key,
      label: key,
      amount: this.sum(rows.map(row => row.totalAmountValue))
    }));
  }

  private options(values: string[], allLabel: string) {
    const unique = [...new Set(values)].sort((a, b) => a.localeCompare(b, 'es'));
    return [{ label: allLabel, value: null }, ...unique.map(value => ({ label: value, value }))];
  }

  private group<T>(values: T[], selector: (value: T) => string): Map<string, T[]> {
    const result = new Map<string, T[]>();
    for (const value of values) {
      const key = selector(value);
      result.set(key, [...(result.get(key) ?? []), value]);
    }
    return result;
  }

  private compareFields<T extends object>(left: T, right: T, fields: Array<keyof T>): number {
    return fields.map(field => String(left[field])).join('\u0001')
      .localeCompare(fields.map(field => String(right[field])).join('\u0001'), 'es');
  }

  private decimalKey(value: ExactDecimal): string {
    return `${value.coefficient}:${value.scale}`;
  }

  private requiredText(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private nullableText(value: unknown): string | null {
    const text = this.requiredText(value);
    return text || null;
  }

  private displayText(value: unknown): string {
    return this.nullableText(value) ?? EMPTY_LABEL;
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }
}
